import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import useBodyScrollLock from "hooks/useBodyScrollLock";
import { callGetAvailableQuotes, callSendQuoteResponse } from "api/MuyeonApi";
import { quoteCategoryLabel } from "constants/quoteCategories";
import { useAuth } from "context/AuthContext";
import { canReceiveQuoteAs, heldTypes } from "constants/permissions";
import { openChatRoom } from "common/nativeBridge";
import { openNativeOrWeb } from "common/nativeRoute";
import { useNetBusy } from "components/ui/EmptyView";

/**
 * 받은 견적요청 팝업(전역 오버레이) — 기존 CommonModal 하단시트 규칙 재사용.
 *  - 딥링크/메뉴가 ?quotesPopup=1 로 진입하면 홈(메인탭) 위로 팝업이 올라온다(탭 안에 갇히지 않음).
 *  - 전공 미등록이면 등록 안내, 있으면 요청 리스트(탭 → 상세에서 견적 보내기).
 */
export default function ReceivedQuotesPopup() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, activeType } = useAuth();
  // 받은 견적요청은 강사/학원 '활동 중'일 때만 열람 (일반회원·무용수 활동 중에는 팝업 자체 비노출, 2026-09-26)
  const show = params.get("quotesPopup") === "1" && canReceiveQuoteAs(activeType);
  // 강사를 보유했는데 다른 유형으로 활동 중이면(새 견적요청 푸시를 눌러 들어온 경우) 조용히 사라지지 않게
  //  전환 화면이 있는 요청 목록으로 보낸다. 보유하지 않았으면 종전처럼 아무것도 띄우지 않는다.
  const needsSwitch = params.get("quotesPopup") === "1" && !canReceiveQuoteAs(activeType)
    && ["TEACHER", "ACADEMY"].some((t) => heldTypes(user).includes(t));
  useEffect(() => {
    if (needsSwitch) navigate("/availableQuotes", { replace: true });
  }, [needsSwitch, navigate]);
  const netBusy = useNetBusy(); // 로딩 중 빈 문구 숨김(전역 규칙)
  const [state, setState] = useState(null); // { needsGenre, items }
  const [sendingId, setSendingId] = useState(null); // 즉시 발송 중인 요청 id
  const [toast, setToast] = useState("");           // 발송 결과/오류 안내
  useBodyScrollLock(show);

  useEffect(() => {
    if (!show) return;
    setState(null);
    callGetAvailableQuotes()
      .then((r) => {
        const d = r.data;
        if (Array.isArray(d)) setState({ needsGenre: false, items: d });
        else setState({ needsGenre: !!d?.needsGenre, items: Array.isArray(d?.items) ? d.items : [] });
      })
      .catch(() => setState({ needsGenre: false, items: [] }));
  }, [show]);

  if (!show) return null;

  const close = () => {
    const next = new URLSearchParams(params);
    next.delete("quotesPopup");
    setParams(next, { replace: true });
  };
  const go = (path) => { close(); navigate(path); };

  // 원터치 즉시 발송 — 폼 없이 강사 기본값(자동견적 금액 + 자기소개/인사)으로 견적 전송.
  //  전송 즉시 백엔드가 채팅방+견적카드를 만들고 roomId 반환 → 그 채팅방으로 이동해 발송 내용 확인.
  const sendInstant = async (q) => {
    if (sendingId) return;
    setSendingId(q.id);
    setToast("");
    try {
      const r = await callSendQuoteResponse(q.id, {}); // 기본값 발송
      const rid = r?.data?.roomId ?? null;
      // 보낸 요청은 목록에서 제거
      setState((s) => (s ? { ...s, items: s.items.filter((x) => x.id !== q.id) } : s));
      // 앱: 채팅방으로 이동 → 발송한 견적카드 확인. 웹: 채팅 화면이 없어 안내만(silent).
      if (rid && openNativeOrWeb(openChatRoom, { roomId: rid }, { silent: true })) {
        close();
      } else {
        setToast("견적을 보냈어요. 고객 채팅방에 견적카드가 전달됐어요.");
      }
    } catch (e) {
      const m = e?.response?.data?.message;
      setToast(typeof m === "string" ? m : "견적 전송에 실패했어요.");
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div data-id="modal-bottom" className="modal-bottom-dimmed is-show">
      <div className="modal-bottom">
        <div className="tit-box">
          <p>들어온 견적 요청</p>
          <button onClick={close} type="button" className="ico-btn">
            <i className="close"></i>
            <span className="visually-hidden"> 닫기 </span>
          </button>
        </div>

        <div className="con-box">
          {state === null ? (
            <p className="t-side">불러오는 중…</p>
          ) : state.needsGenre ? (
            <>
              <p className="t-body">전공(장르)을 먼저 등록해 주세요</p>
              <p className="t-side">레슨 전공(장르)을 등록하면, 내 전공에 맞는 견적요청만 받아볼 수 있어요.</p>
            </>
          ) : state.items.length ? (
            /* 이력서/레슨 프로필과 동일한 meal-record-card(루트 스코프) 재활용 + 내부 스크롤.
               (모달 안이라 #meal_record_add 하위 meal-card-list 는 못 쓰고, 카드 간격은 gap 으로 처리) */
            <div style={{ maxHeight: "56vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: "1.2rem", padding: "0.2rem 0.4rem 0.6rem", margin: "0 -0.4rem" }}>
              {state.items.map((q) => (
                <div className="card line meal-record-card" key={q.id}>
                  <div className="head">
                    <span className="round-btn-sm filled primary">{quoteCategoryLabel(q.categoryId)}</span>
                    <span style={{ fontSize: "1.3rem", color: "#6D6E71" }}>{(q.createdAt || "").slice(0, 10)}</span>
                  </div>
                  <div className="body" onClick={() => go(`/availableQuotes/${q.id}`)} style={{ cursor: "pointer" }}>
                    <div className="meal-info">
                      <p className="tit">{quoteCategoryLabel(q.categoryId)} 레슨 견적요청</p>
                      <p className="meal-list">{q.region || "지역 미지정"}</p>
                    </div>
                  </div>
                  {/* 상세(문진 답변 열람) + 원터치 발송(기본값) */}
                  <div className="btn-group" style={{ gap: "0.6rem" }}>
                    <button type="button" className="round-btn-sm outlined"
                      style={{ flex: 1, whiteSpace: "nowrap", fontSize: "1.3rem" }}
                      onClick={() => go(`/availableQuotes/${q.id}`)}>상세 보기</button>
                    <button type="button" className="round-btn-sm filled primary"
                      style={{ flex: 1, whiteSpace: "nowrap", fontSize: "1.3rem" }}
                      disabled={sendingId === q.id}
                      onClick={() => sendInstant(q)}>
                      {sendingId === q.id ? "보내는 중…" : "제안 보내기"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            netBusy ? null : <p className="t-side">응답 가능한 견적요청이 없어요.</p>
          )}
          {toast && <p className="t-side" style={{ marginTop: "0.8rem" }}>{toast}</p>}
        </div>

        <div className="btn-group">
          {state?.needsGenre ? (
            <button type="button" className="btn-md filled primary" onClick={() => go("/lessonGenres")}>전공 등록하러 가기</button>
          ) : (
            <button type="button" className="btn-md filled primary" onClick={close}>닫기</button>
          )}
        </div>
      </div>
    </div>
  );
}
